import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

/**
 * Small header chip showing single-holding concentration risk. Pulls
 * from /api/pf-health every 5 min. Only renders when there's
 * a HIGH (>=20%) or MED (10-20%) concentration — stays invisible
 * when the portfolio is well-diversified.
 *
 * Click to run /concentration in the terminal.
 */

interface HealthSummary {
  concentration?: {
    high_count: number
    med_count: number
    top3?: Array<{ name: string; pct_of_tracked: number }>
  }
}

const REFRESH_MS = 5 * 60_000

export function ConcentrationChip({ onClick }: { onClick?: () => void }) {
  const [data, setData] = useState<HealthSummary | null>(null)

  useEffect(() => {
    let alive = true
    const fetchOnce = async () => {
      try {
        const r = await api.get<HealthSummary>('/api/pf-health')
        if (alive) setData(r.data)
      } catch {
        // silent — chip is non-essential
      }
    }
    fetchOnce()
    const t = setInterval(fetchOnce, REFRESH_MS)
    return () => { alive = false; clearInterval(t) }
  }, [])

  const conc = data?.concentration
  if (!conc) return null
  const high = conc.high_count ?? 0
  const med = conc.med_count ?? 0
  if (high === 0 && med === 0) return null

  const top = (conc.top3 ?? [])[0]
  const tone = high > 0 ? 'bg-red-500/20 text-red-400' : 'bg-crt-amber/20 text-crt-amber'
  const label = high > 0
    ? `${high} HIGH`
    : `${med} MED`

  return (
    <button
      type="button"
      onClick={onClick}
      title={
        top
          ? `Concentration risk — largest holding "${top.name}" is ${top.pct_of_tracked.toFixed(1)}% of tracked NAV. Click to /concentration.`
          : 'Concentration risk — click to /concentration'
      }
      className={`${tone} font-mono text-[10px] px-1.5 py-0.5 rounded border border-current/30 hover:brightness-125 transition-all`}
    >
      ◆ {label}
    </button>
  )
}
